import { useApolloClient } from "@apollo/client";
import { json2csv } from "json-2-csv";
import { Interval } from "../Calendar/Interval.tsx";
import { downloadCsvFile } from "../DataUtils/downloadCsvFile.ts";
import { useAllFranchisees } from "../Franchisee/useAllFranchisees.ts";
import { FranchiseeItemFragment } from "../gql/graphql.ts";
import { useWithLoading } from "../UI/Loading/useWithLoading.ts";
import { useUserGroupName } from "../UserGroup/useUserGroupName.ts";
import { createBookedClassRoomsCsvRows } from "./createBookedClassRoomsCsvRows.ts";
import { BookedClassRoomDto } from "./types.ts";
import {
  GetAvailableTeacherSlotsQuery,
  GetAvailableTeacherSlotsQueryResult,
} from "./useAvailableTeacherSlots.ts";
import {
  GetBookedClassRoomsQuery,
  GetBookedClassRoomsQueryResult,
} from "./useBookedClassRooms.ts";
import {
  GetClassRoomsWhereTeacherWasCandidateQuery,
  GetClassRoomsWhereTeacherWasCandidateQueryResult,
} from "./useTeacherCandidateClassRooms.ts";

const csvColumns = [
  { field: "weekStart", title: "Week start" },
  { field: "date", title: "Date" },
  { field: "startTime", title: "Start" },
  { field: "endTime", title: "End" },
  { field: "teacher", title: "Teacher" },
  { field: "teacherEmail", title: "Teacher email" },
  { field: "status", title: "Status" },
  { field: "classType", title: "Class type" },
  { field: "userGroup", title: "User group" },
  { field: "classRoomId", title: "Classroom ID" },
];

function toDateRangeVariables(interval: Interval) {
  return {
    fromDate: interval.fromDate.toISOString(),
    toDate: interval.toDate.toISOString(),
  };
}

function createCsvFileName(interval: Interval): string {
  const dateFormat = "YYYY-MM-DD";
  return `booked_classrooms_${interval.fromDate.format(dateFormat)}_to_${interval.toDate.format(dateFormat)}.csv`;
}

export function useExportBookedClassRoomsCsv() {
  const apolloClient = useApolloClient();
  const { franchisees } = useAllFranchisees();
  const getUserGroupName = useUserGroupName();
  const { withLoading, loading } = useWithLoading();

  async function fetchBookedClassRooms(interval: Interval) {
    const { data } = await apolloClient.query<GetBookedClassRoomsQueryResult>({
      query: GetBookedClassRoomsQuery,
      variables: toDateRangeVariables(interval),
      fetchPolicy: "network-only",
    });
    return data.getBookedClassRooms;
  }

  async function fetchAvailableTeacherSlots(interval: Interval) {
    const { data } =
      await apolloClient.query<GetAvailableTeacherSlotsQueryResult>({
        query: GetAvailableTeacherSlotsQuery,
        variables: toDateRangeVariables(interval),
        fetchPolicy: "network-only",
      });
    return data.getAvailableTeacherSlots;
  }

  async function fetchCandidateClassRoomIds(
    teacherId: string,
    classRooms: BookedClassRoomDto[],
  ) {
    const { data } =
      await apolloClient.query<GetClassRoomsWhereTeacherWasCandidateQueryResult>(
        {
          query: GetClassRoomsWhereTeacherWasCandidateQuery,
          variables: {
            teacherId,
            classRoomIds: classRooms.map((c) => c.classRoomId),
          },
          fetchPolicy: "network-only",
        },
      );
    return new Set(data.getClassRoomsWhereTeacherWasCandidate);
  }

  async function exportBookedClassRoomsCsv({
    interval,
    teacherId,
  }: {
    interval: Interval;
    teacherId: string | null;
  }) {
    await withLoading(async () => {
      const [classRooms, availableSlots] = await Promise.all([
        fetchBookedClassRooms(interval),
        fetchAvailableTeacherSlots(interval),
      ]);

      const teacherFilter = teacherId
        ? {
            teacherId,
            candidateClassRoomIds: await fetchCandidateClassRoomIds(
              teacherId,
              classRooms,
            ),
          }
        : undefined;

      const rows = createBookedClassRoomsCsvRows({
        classRooms,
        availableSlots,
        teacherFilter,
        teacherById: new Map<string, FranchiseeItemFragment>(
          franchisees.map((f) => [f.id, f]),
        ),
        getUserGroupName,
      });

      downloadCsvFile({
        content: json2csv(rows, { keys: csvColumns, excelBOM: true }),
        fileName: createCsvFileName(interval),
      });
    });
  }

  return {
    exportBookedClassRoomsCsv,
    loading,
  };
}
